import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Rocket, Loader2, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Badge } from "@/components/ui/badge";

/**
 * WO9 Section B roll-up — every reputation-enabled site, ready or not, with the failing rows named.
 * Staff only. Read-only: the N/A control lives on the site's own Reputation tab.
 */
export default function GoLive() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/readiness");
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not load the roll-up");
        setData({ rows: [], ready: 0, total: 0 });
      }
    })();
  }, []);

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="golive-page">
        <div>
          <div className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Go-live</h1>
          </div>
          <p className="mt-1 text-base text-muted-foreground">
            Which sites are actually proven. Open a site to see the evidence or mark a row N/A.
          </p>
        </div>

        {!data ? (
          <div className="flex justify-center py-16 text-muted-foreground" data-testid="golive-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="rounded-md border border-border bg-card px-4 py-3">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Ready</p>
                <p className="font-display text-2xl font-semibold" data-testid="golive-ready-count">
                  {data.ready} / {data.total}
                </p>
              </div>
              <p className="max-w-md text-xs text-muted-foreground" data-testid="golive-note">{data.note}</p>
            </div>

            {!data.rows.length ? (
              <p className="text-sm text-muted-foreground" data-testid="golive-empty">
                No reputation-enabled sites yet.
              </p>
            ) : (
              <div className="space-y-2" data-testid="golive-rows">
                {data.rows.map((r) => (
                  <div key={r.site_id} className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3"
                    data-testid={`golive-row-${r.site_id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {r.ready
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          : <XCircle className="h-4 w-4 text-red-500" />}
                        <p className="font-medium">{r.business_name || r.url}</p>
                        {r.ready ? (
                          <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                            data-testid={`golive-badge-${r.site_id}`}>Ready</Badge>
                        ) : (
                          <Badge className="border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400"
                            data-testid={`golive-badge-${r.site_id}`}>Not ready</Badge>
                        )}
                      </div>
                      <p className="mt-0.5 break-all text-xs text-muted-foreground">{r.url}</p>
                      {!!r.failing.length && (
                        <ul className="mt-1.5 space-y-0.5 text-xs text-red-600 dark:text-red-400"
                          data-testid={`golive-failing-${r.site_id}`}>
                          {r.failing.map((f) => <li key={f.id}>· {f.label}</li>)}
                        </ul>
                      )}
                      {(!!r.na.length || !!r.off.length) && (
                        <p className="mt-1 text-xs text-muted-foreground" data-testid={`golive-extras-${r.site_id}`}>
                          {r.na.length ? `${r.na.length} marked N/A` : ""}
                          {r.na.length && r.off.length ? " · " : ""}
                          {r.off.length ? `${r.off.length} not in use` : ""}
                        </p>
                      )}
                    </div>
                    <Link to={`/site/${r.site_id}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      data-testid={`golive-open-${r.site_id}`}>
                      Open <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
